import React, { FunctionComponent, useRef } from 'react';
import withStyles from 'react-jss';
import classnames from 'classnames';
import { isEmpty, map, merge } from 'ramda';
import { Grid, Header, Icon } from 'semantic-ui-react';
import {
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
  Bar,
  XAxis,
  YAxis,
  Line,
  ComposedChart,
} from 'recharts';

import {
  tickFormatter,
  chartMargin,
  Data,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantLift/MerchantLift.helpers';
import DownloadWidget from 'components/DownloadWidget';
import { ClassProp } from 'helpers/types';
import { theme } from 'config';
import { getBaseStyles } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantLift/MerchantLift.styles';
import TickFormatter from 'components/TickFormatter';
import CustomTooltip from 'components/CustomTooltip';

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
  data: Data;
};

const { violet, softGray, coral } = theme;

const styles = getBaseStyles({
  headerIcon: {
    fontSize: '1em !important',
    marginRight: '.625em !important',
    color: `${softGray} !important`,

    '&.exposed': {
      marginLeft: '1rem !important',
      color: `${violet} !important`,
    },
  },
});

const tooltipFields = (average: number) => [
  {
    label: 'Control Group',
    value: 'scaledControlSales',
  },
  {
    label: 'Treatment Group',
    value: 'exposedTotalSales',
  },
  {
    label: 'Average',
    value: average,
  },
];

const lineMapping = (average: number) =>
  map((controlGroup) => merge(controlGroup, { average }));

const renderHeader = (classes: Styles) => (
  <Header as="h3" className={classes.header}>
    <span>
      <Icon className={classes.headerIcon} name="chart bar" />
      Control Group
    </span>

    <span>
      <Icon
        className={classnames(classes.headerIcon, 'exposed')}
        name="chart bar"
      />
      Treatment Group
    </span>
  </Header>
);

const Lift: FunctionComponent<Props> = ({
  classes,
  data: { results, exposedAverage },
}) => {
  const contentRef = useRef(null);
  const average = exposedAverage || 0;
  const lineData = lineMapping(average)(results);

  return (
    <>
      <div ref={contentRef} className={classes.ref}>
        <Grid.Row centered className={classes.chartContent}>
          <Grid.Column
            width={15}
            columns={1}
            textAlign="center"
            verticalAlign="middle"
          >
            <Grid.Column>
              {isEmpty(results) ? (
                <Header as="h3">
                  There is no data to display at this time.
                </Header>
              ) : (
                <div className={classes.chart}>
                  <ResponsiveContainer>
                    <ComposedChart data={results} margin={chartMargin}>
                      <CartesianGrid vertical={false} />
                      <YAxis tickFormatter={tickFormatter()} />

                      <XAxis
                        interval={0}
                        dataKey="periodStart"
                        tick={<TickFormatter data={results} />}
                      />

                      <Tooltip
                        content={
                          <CustomTooltip
                            xAxis={{ label: 'Day', key: 'periodStart' }}
                            fields={tooltipFields(average)}
                          />
                        }
                      />

                      <Bar
                        animationBegin={750}
                        dataKey="scaledControlSales"
                        fill={softGray}
                      />

                      <Bar
                        animationBegin={750}
                        dataKey="exposedTotalSales"
                        fill={violet}
                      />

                      <Line
                        data={lineData}
                        strokeDasharray="5 5"
                        type="monotone"
                        dataKey="average"
                        stroke={coral}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Grid.Column>
          </Grid.Column>

          <Grid.Column width={16} className={classes.legend}>
            {renderHeader(classes)}
          </Grid.Column>
        </Grid.Row>
      </div>

      <DownloadWidget
        nodeRef={contentRef}
        className={classes.download}
        options={['PNG', 'PDF']}
        imageName={'Lift'}
      />
    </>
  );
};

export default withStyles(styles)(Lift);
